import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { addDays } from "date-fns";
import { 
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Calendar, 
  Clock,
  Download,
  FileJson, 
  FileSpreadsheet, 
  FileText as FilePdf, 
  RefreshCw,
  Settings} from "lucide-react";
import { useState } from "react";
import { DateRange } from "react-day-picker";

const mockReports = [
  {
    id: 1,
    title: "Фінансовий звіт",
    type: "financial",
    period: "Січень 2024",
    status: "generated",
    createdAt: "2024-02-01T10:00:00",
    formats: ["pdf", "xlsx", "csv"]
  },
  {
    id: 2,
    title: "Звіт по орендарях",
    type: "tenants",
    period: "Лютий 2024",
    status: "pending",
    createdAt: "2024-02-15T15:30:00",
    formats: ["pdf", "xlsx"]
  },
  {
    id: 3,
    title: "Аналіз доходів",
    type: "analytics",
    period: "Q1 2024",
    status: "scheduled",
    createdAt: "2024-03-01T00:00:00",
    formats: ["pdf", "xlsx", "csv"]
  }
];

const reportTemplates = [
  {
    id: 1,
    name: "Базовий фінансовий звіт",
    description: "Загальна інформація про доходи та витрати",
    type: "financial"
  },
  {
    id: 2,
    name: "Детальний звіт по об'єктах",
    description: "Повна інформація про всі об'єкти нерухомості",
    type: "properties"
  },
  {
    id: 3,
    name: "Звіт по заборгованостях",
    description: "Аналіз прострочених платежів та боргів",
    type: "debts"
  }
];

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState("generate");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(),
    to: addDays(new Date(), 30)
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "generated":
        return "bg-emerald-500/15 text-emerald-600";
      case "pending":
        return "bg-amber-500/15 text-amber-600";
      case "scheduled":
        return "bg-sky-500/15 text-sky-600";
      default:
        return "bg-slate-500/15 text-slate-600";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "generated":
        return "Згенеровано";
      case "pending":
        return "В обробці";
      case "scheduled":
        return "Заплановано";
      default:
        return status;
    }
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case "pdf":
        return <FilePdf className="h-4 w-4" />;
      case "xlsx":
        return <FileSpreadsheet className="h-4 w-4" />;
      case "csv":
        return <FileJson className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Звіти</h1>
          <p className="text-muted-foreground">
            Генерація та управління звітами
          </p>
        </div>
        <Button variant="outline" size="icon">
          <Settings className="h-5 w-5" />
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Згенеровано звітів
            </CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">28</div>
            <div className="flex items-center text-sm text-muted-foreground">
              <ArrowUpRight className="mr-1 h-4 w-4 text-emerald-500" />
              +15% цього місяця
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Заплановані звіти
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">5</div>
            <div className="flex items-center text-sm text-muted-foreground">
              <Clock className="mr-1 h-4 w-4" />
              Наступний через 2 дні
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Автоматичні звіти
            </CardTitle>
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">8</div>
            <div className="flex items-center text-sm text-muted-foreground">
              <ArrowUpRight className="mr-1 h-4 w-4 text-emerald-500" />
              Всі активні
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Шаблони
            </CardTitle>
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{reportTemplates.length}</div>
            <div className="flex items-center text-sm text-muted-foreground">
              <ArrowUpRight className="mr-1 h-4 w-4 text-emerald-500" />
              +2 нових
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Створити звіт
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Історія
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Шаблони
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate">
          <Card>
            <CardHeader>
              <CardTitle>Створення нового звіту</CardTitle>
              <CardDescription>
                Оберіть шаблон та налаштуйте параметри звіту
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Шаблон звіту</h4>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger>
                      <SelectValue placeholder="Оберіть шаблон" />
                    </SelectTrigger>
                    <SelectContent>
                      {reportTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id.toString()}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Період</h4>
                  <DatePickerWithRange date={dateRange} setDate={setDateRange} />
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Формат експорту</h4>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <FilePdf className="mr-2 h-4 w-4" />
                      PDF
                    </Button>
                    <Button variant="outline" size="sm">
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Excel
                    </Button>
                    <Button variant="outline" size="sm">
                      <FileJson className="mr-2 h-4 w-4" />
                      CSV
                    </Button>
                  </div>
                </div>

                <Button className="w-full">
                  Згенерувати звіт
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Історія звітів</CardTitle>
              <CardDescription>
                Список згенерованих та запланованих звітів
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Назва</TableHead>
                      <TableHead>Період</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Дата створення</TableHead>
                      <TableHead>Формати</TableHead>
                      <TableHead className="text-right">Дії</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mockReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">
                          {report.title}
                        </TableCell>
                        <TableCell>{report.period}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary"
                            className={getStatusColor(report.status)}
                          >
                            {getStatusText(report.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(report.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {report.formats.map((format) => (
                              <Button 
                                key={format} 
                                variant="ghost" 
                                size="icon"
                                className="h-8 w-8"
                              >
                                {getFormatIcon(format)}
                              </Button>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            <Download className="mr-2 h-4 w-4" />
                            Завантажити
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Шаблони звітів</CardTitle>
              <CardDescription>
                Управління та налаштування шаблонів звітів
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {reportTemplates.map((template) => (
                    <Card key={template.id}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">
                            {template.name}
                          </CardTitle>
                          <Button variant="ghost" size="sm">
                            Редагувати
                          </Button>
                        </div>
                        <CardDescription>
                          {template.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex gap-2">
                          <Badge variant="outline">
                            {template.type}
                          </Badge>
                          <Badge variant="outline">
                            Щомісячний
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage; 